import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookedReserve } from "../../shared/CaseModel";
import randomId from "../../shared/randomId";
import { todayIso } from "../../shared/timeline";
import { amountOf } from "../timeline/CloseStageDialog";

const button = {
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;

/**
 * Records a reserve the insurer booked in its own books: the amount and the
 * day are the user's, empty until entered, the indicated reserve and today
 * one click away; a reason if they give one. Closes with the booking, or with
 * nothing where the user cancels.
 */
export const BookReserveDialog = ({
  indicated,
  onClose,
}: {
  indicated: number;
  onClose: (booking?: Omit<BookedReserve, "partyId">) => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "reserve.history.booked.dialog",
  });
  const today = todayIso();
  const [amount, setAmount] = useState("");
  const [bookedOn, setBookedOn] = useState("");
  const [reason, setReason] = useState("");
  // a reserve below nothing is one the claim is expected to bring in more
  // than it costs: the user may book it
  const value = amountOf(amount, { negative: true });
  const validDate = bookedOn !== "" && bookedOn <= today;

  return (
    <Dialog open onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t("title")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t("description")}</DialogContentText>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <TextField
              size="small"
              required
              label={t("amount")}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              error={amount !== "" && value === undefined}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { inputMode: "decimal" },
              }}
              sx={{ width: 220 }}
            />
            <Button
              variant="outlined"
              sx={button}
              onClick={() =>
                setAmount((Math.round(indicated * 100) / 100).toString())
              }
            >
              {t("useIndicated")}
            </Button>
          </Stack>
          {indicated < 0 && (
            <DialogContentText variant="body2">
              {t("negative")}
            </DialogContentText>
          )}
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <TextField
              type="date"
              size="small"
              required
              label={t("bookedOn")}
              value={bookedOn}
              onChange={(event) => setBookedOn(event.target.value)}
              error={bookedOn !== "" && !validDate}
              helperText={
                bookedOn !== "" && !validDate ? t("future") : undefined
              }
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { max: today },
              }}
              sx={{ width: 220 }}
            />
            <Button
              variant="outlined"
              sx={button}
              onClick={() => setBookedOn(today)}
            >
              {t("today")}
            </Button>
          </Stack>
          <TextField
            size="small"
            label={t("reason")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button sx={button} onClick={() => onClose()}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={button}
          disabled={value === undefined || !validDate}
          onClick={() =>
            onClose({
              id: randomId(),
              amount: value!.toNumber(),
              bookedOn,
              ...(reason.trim() !== "" && { reason: reason.trim() }),
            })
          }
        >
          {t("book")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
